/** Rendering helpers shared by the catalogue, the runner and the history. */

/**
 * 'YYYY-MM-DD' → '30 Aug 2026'.
 *
 * Parsed by hand: `new Date('2026-08-30')` is UTC midnight, which renders as
 * the 29th anywhere west of Greenwich. A report window that reads a day out is
 * exactly the kind of quiet error a reconciliation is supposed to catch.
 */
export function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-PH', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const AS_AT = new Intl.DateTimeFormat('en-PH', {
  timeZone: 'Asia/Manila',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

/**
 * When a snapshot was taken — 'Oct 23, 2026, 2:05 PM (Asia/Manila)'.
 *
 * In Manila with the zone named, NOT the reader's zone as `formatMoment` is: the PDF of the same
 * run prints this instant in Manila, and two copies of one file must not state two moments.
 */
export function formatAsAt(iso: string): string {
  return `${AS_AT.format(new Date(iso))} (Asia/Manila)`;
}

/** A run timestamp, in the reader's own zone — they are reading it now. */
export function formatMoment(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 'YYYY-MM-DD', N days back from today, for the default window. */
export function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const today = (): string => daysAgo(0);

/**
 * A measure cell.
 *
 * Money arrives as a centavo string and must never become a float on the way to
 * the screen — `Number('3383999661')` is safe today and silently wrong the day
 * a figure passes 2^53. It is split as text and only the peso part is grouped.
 */
export function formatCell(value: string | null, kind: string): string {
  if (value === null || value === '') return '—';
  if (kind === 'money') {
    const negative = value.startsWith('-');
    const digits = (negative ? value.slice(1) : value).padStart(3, '0');
    const pesos = digits.slice(0, -2);
    const centavos = digits.slice(-2);
    const grouped = pesos.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${negative ? '-' : ''}₱${grouped}.${centavos}`;
  }
  if (kind === 'count') {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  if (kind === 'rate') return `${value}%`;
  return value;
}

/** '18.4 kB'. Sizes are shown so nobody opens a 40 MB file on tethered data. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * A five-field cron, in words.
 *
 * Only the shapes the console can produce are named; anything else is shown
 * verbatim rather than guessed at. A wrong plain-English reading of a schedule
 * is worse than no reading — an operator trusts it and stops checking.
 */
const at = (h: string, m: string): string => `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;

export function describeCron(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [min, hour, dom, mon, dow] = parts as [string, string, string, string, string];
  if (dom === '*' && mon === '*' && dow === '*') {
    if (/^\*\/\d+$/.test(hour)) return `every ${hour.slice(2)}h`;
    if (/^\d+$/.test(hour) && /^\d+$/.test(min)) return `daily at ${at(hour, min)}`;
  }
  if (dom === '*' && mon === '*' && /^\d$/.test(dow) && /^\d+$/.test(hour)) {
    const day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][Number(dow)];
    return `${day} at ${at(hour, min)}`;
  }
  if (/^\d+$/.test(dom) && mon === '*' && /^\d+$/.test(hour)) {
    return `day ${dom} at ${at(hour, min)}`;
  }
  return cron;
}
